// The owner's switches, the same on the server and in the app. Each section of a circle (its place in the navigation)
// can be turned off for everyone while it's being tried out or kept for some people: off, it leaves the navigation and
// its API refuses. Nothing in it is deleted, and Keep a copy still includes it. Invites can be open to everyone or
// only to the accounts the owner picks; the owner can always invite.
export const SECTIONS = ['home', 'people', 'photos', 'moments', 'recipes', 'calendar'] as const
export type Section = (typeof SECTIONS)[number]
export type Sections = Record<Section, boolean>

export const SECTION_LABELS: Record<Section, string> = {
  home: 'Home',
  people: 'People',
  photos: 'Photos',
  moments: 'Moments',
  recipes: 'Recipes',
  calendar: 'Calendar',
}

export const ALL_SECTIONS_ON: Sections = {
  home: true,
  people: true,
  photos: true,
  moments: true,
  recipes: true,
  calendar: true,
}

export const INVITE_MODES = ['everyone', 'chosen'] as const
export type InviteMode = (typeof INVITE_MODES)[number]

// How Home is laid out for everyone. Traditional: the welcome, a door into each section, On this day and Lately.
// Modern: the circle you picked is the page's title, then rings for On this day and each person who added something,
// what's new since you last looked as one blend of photos, recipes and moments that ends, and Wander's shelves. On a
// tablet or computer the circle picker also moves to the top left. Both show the same things to the same people.
export const HOME_STYLES = ['traditional', 'modern'] as const
export type HomeStyle = (typeof HOME_STYLES)[number]

// How Photos is laid out for everyone. Traditional: albums first, shelved in collections, and the stream of every
// photo in rows. Modern: rings along the top for On this day and the people with new photos, tags that narrow a wall
// of whole photos, and each photo opening as a post with the photos from the same day under it. Both show the same
// photos to the same people; only the layout changes.
export const PHOTO_STYLES = ['traditional', 'modern'] as const
export type PhotoStyle = (typeof PHOTO_STYLES)[number]

// What each circle can turn on or off for itself, under the owner's switches above: "Grow this circle". Home and the
// circle's own page (who's here, invites) are always there. People here means the family tree: with it off, the
// People page is just the circle's members, as when the owner turns People off.
export const CIRCLE_SECTIONS = ['photos', 'moments', 'recipes', 'people', 'calendar'] as const
export type CircleSection = (typeof CIRCLE_SECTIONS)[number]

export const CIRCLE_SECTION_LABELS: Record<CircleSection, string> = {
  photos: 'Photos',
  moments: 'Moments',
  recipes: 'Recipes',
  people: 'Family tree',
  calendar: 'Calendar',
}

// What a circle shows: on only where both the owner and the circle have it on.
export const shown = (app: Sections, circle?: Partial<Record<CircleSection, boolean>>): Sections => ({
  ...app,
  ...Object.fromEntries(CIRCLE_SECTIONS.map((s) => [s, app[s] && circle?.[s] !== false])),
})

// What a new circle is for. A starter only picks what's on to begin with and what kind of circle it is (a family
// circle has homes); any circle can turn anything on later.
export const STARTERS = ['family', 'friends', 'group', 'stories'] as const
export type Starter = (typeof STARTERS)[number]

export const STARTER_DETAILS: Record<
  Starter,
  { label: string; hint: string; kind: 'family' | 'casual'; on: CircleSection[]; example: string }
> = {
  family: {
    label: 'Family',
    hint: 'Your relatives and the friends who are family. A family tree, a page for each home, recipes, photos and moments.',
    kind: 'family',
    on: ['photos', 'moments', 'recipes', 'people', 'calendar'],
    example: 'e.g. The Rivera family',
  },
  friends: {
    label: 'Friends',
    hint: 'People you want to keep up with. Photos and moments, with no family tree to fill in.',
    kind: 'casual',
    on: ['photos', 'moments', 'calendar'],
    example: 'e.g. College friends',
  },
  group: {
    label: 'Group',
    hint: 'A church group, a team, a club. Photos and recipes to share together.',
    kind: 'casual',
    on: ['photos', 'recipes', 'calendar'],
    example: 'e.g. Saturday soccer moms',
  },
  stories: {
    label: 'Stories',
    hint: 'Moments come first: stories told together, with their photos.',
    kind: 'casual',
    on: ['moments', 'photos', 'calendar'],
    example: 'e.g. Grandpa’s stories',
  },
}

// How Moments is laid out for everyone. Traditional: the feed, cards or list. Modern: rings along the top for people
// with moments written as they happened, All / Then / Now tabs, a Filter for whose, when and who was there, and a feed
// or cards that draw each moment the way its author chose, each opening as a post. Both show the same moments to the same
// people; only the layout changes.
export const MOMENT_STYLES = ['traditional', 'modern'] as const
export type MomentStyle = (typeof MOMENT_STYLES)[number]

// How Recipes is laid out for everyone. Traditional: books on their shelf, and every recipe in tiles. Modern: the
// kitchens in rings along the top, chips and an ingredient box that narrow a wall of whole dishes that turn over to
// their cards, each recipe opening as a post, and Cook a step at a time. Both show the same recipes to the same
// people; only the layout changes.
export const RECIPE_STYLES = ['traditional', 'modern'] as const
export type RecipeStyle = (typeof RECIPE_STYLES)[number]

// How the Calendar is laid out for everyone. Traditional: the month as a grid beside a list of its days. Modern: who's
// coming up in rings along the top, then each day that has something on it as a card of its own. Both show the same
// days to the same people; only the layout changes.
export const CALENDAR_STYLES = ['traditional', 'modern'] as const
export type CalendarStyle = (typeof CALENDAR_STYLES)[number]

// How People is laid out for everyone. Traditional: Connections as a compact list, a row a person. Modern: everyone as
// a face in a ring, row after row. Both show the same people to the same people; only the layout changes.
export const PEOPLE_STYLES = ['traditional', 'modern'] as const
export type PeopleStyle = (typeof PEOPLE_STYLES)[number]
